<template>
    <minimizable-modal :name="chainID + '_joint'"
                       :draggable="'.v-app-bar'"
                       :width="400"
                       height="auto"
                       :reset="true"
                       :clickToClose="false"
                       overlayTransition=""
                       transition=""
                       :shiftX="modalShift.x"
                       :shiftY="modalShift.y"
                       :minimized.sync="minimized"
                       :visible.sync="visible"
    >
        <v-app-bar color="primary" @dblclick="minimized = !minimized">
            <v-toolbar-title>
                {{ chain.name }} - Create Joint
            </v-toolbar-title>
            <v-spacer></v-spacer>

            <v-tooltip top>
                <template v-slot:activator="{ on }">
                    <v-btn icon
                           dark
                           v-on="on"
                           @click.stop="minimized = !minimized"
                           @dblclick.stop="">
                        <v-icon>{{ minimized ? 'maximize' : 'minimize' }}</v-icon>
                    </v-btn>
                </template>
                <span>{{ minimized ? 'Maximize' : 'Minimize' }}</span>
            </v-tooltip>
            <v-tooltip top>
                <template v-slot:activator="{ on }">
                    <v-btn icon
                           dark
                           v-on="on"
                           @click.stop="hide"
                           @dblclick.stop="">
                        <v-icon>close</v-icon>
                    </v-btn>
                </template>
                <span>Close</span>
            </v-tooltip>
        </v-app-bar>

        <v-card v-if="!minimized">
            <div class="pl-5 pr-5" >
                <v-layout ma-0 pa-0 mr-3>
                    <v-flex my-0 py-0 xs3 style="text-align: right">
                        <v-card flat>
                            <v-card-text style="text-align: left;">Volume</v-card-text>
                        </v-card>
                    </v-flex>
                    <v-flex my-0 py-0 xs5 style="text-align: left">
                        <v-text-field my-0 py-0
                                      readonly
                                      v-model="joint.volumes"
                                      background-color="#5a6268"
                                      style="font-size: 12px">
                        </v-text-field>
                    </v-flex>
                    <v-flex my-0 py-0 xs3>
                            <v-btn @click="onClickVolumePick" :disabled="state.runningDemo"
                                   style="width: 100%; height: 100%">
                                {{ state.volumingPicking ? (flags.selectMultiple ? "Done" : "Cancel") : "Pick"}}
                            </v-btn>
                    </v-flex>
                    <v-flex my-0 py-0 xs1>
                        <div class="relativeContainer">
                            <v-btn icon class="settingsIcon" menuopen="0" :disabled="state.runningDemo" @click="toggleSettingsMenu">
                                <v-icon>settings</v-icon>
                            </v-btn>
                            <div class="settingsMenu" ref="volumePickingSettings">
                                <v-checkbox v-model="flags.selectMultiple" style="margin-top: 0" hide-details label="Select multiple"></v-checkbox>
                                <v-checkbox v-model="flags.selectGroup" style="margin-top: 0" hide-details label="Select group"></v-checkbox>
                            </div>
                        </div>
                    </v-flex>
                </v-layout>
                <div v-if="joint.volumes.length > 0">
                    <v-layout ma-0 pa-0 mr-3>
                        <v-flex my-0 py-0 xs3 style="text-align: right">
                            <v-card flat>
                                <v-card-text style="text-align: left;">Joint Type</v-card-text>
                            </v-card>
                        </v-flex>
                        <v-flex my-0 py-0 xs9 style="text-align: left">
                            <drop-down :disabled="state.runningDemo"
                                    v-model="joint.type"
                                    :options="jointTypes">
                            </drop-down>
                        </v-flex>
                    </v-layout>
                    <fieldset v-if="joint.type === 'Rigid'" :disabled="state.runningDemo">
                        <rigid-joint :parent="viewContainer" :joint="joint" :chain="chain" :volumeMesh="mesh"></rigid-joint>
                    </fieldset>
                    <fieldset v-else-if="joint.type === 'Ball'" :disabled="state.runningDemo">
                        <ball-joint :parent="viewContainer" :joint="joint" :chain="chain" :volumeMesh="mesh"></ball-joint>
                    </fieldset>
                    <fieldset v-else-if="joint.type === 'Hinge'" :disabled="state.runningDemo">
                        <hinge-joint :parent="viewContainer" :joint="joint" :chain="chain" :volumeMesh="mesh"></hinge-joint>
                    </fieldset>
                    <fieldset v-else-if="joint.type === 'Slider'" :disabled="state.runningDemo">
                        <slider-joint :parent="viewContainer" :joint="joint" :chain="chain" :volumeMesh="mesh"></slider-joint>
                    </fieldset>
                </div>
            </div>
            <v-card-actions class="justify-center">
                <div>
                    <a @click="executeDemo()">
                        {{ state.runningDemo ? "Stop" : "Try It Out" }}
                    </a>
                </div>
            </v-card-actions>
            <v-card-actions class="justify-center">
                <div>
                    <v-btn :disabled="state.runningDemo || joint.volumes.length <= 0" @click="addJoint()" color="success">
                        Create Joint
                    </v-btn>
                </div>
            </v-card-actions>
        </v-card>
    </minimizable-modal>
</template>

<script>
    import {mapState, mapActions} from 'vuex';
    import {
        RigidJoint,
        BallJoint,
        HingeJoint,
        SliderJoint
    } from './joints';
    import MinimizableModal from '../../../components/minimizable-modal'
    import DropDown from '../../../components/dropdown';

    const {
        Group,
        Vector3,
        Matrix4
    } = THREE;

    export default {
        name: "JointCreator",

        components: {
            DropDown,
            RigidJoint,
            BallJoint,
            HingeJoint,
            SliderJoint,
            MinimizableModal
        },

        props: {
            bus: {
                required: true
            },
            chainID: {
                required: true
            }
        },

        computed: {
            ...mapState('kinematics', {
                chains: 'chains'
            }),

            chain: {
                get() {
                    return this.chains.find(chain => chain.id === this.chainID);
                }
            },
        },

        watch: {
            'joint.volumes': {
                handler () {
                    this.updateHighlightedMesh();

                    if (this.joint.volumes.length > 0) {
                        this.joint.currentPointMatrix.copy(getVolumeWorldMatrix(this.joint.volumes[0])).invert();
                    } else {
                        this.joint.currentPointMatrix.identity();
                    }
                }
            },
        },

        data() {
            return {
                name: "createJoint",
                state: {
                    volumingPicking: false,
                    runningDemo: false
                },
                flags: {
                    selectMultiple: false,
                    selectGroup: false
                },
                jointTypes: {
                    items: [
                        {key: "Rigid", value: "Rigid"},
                        {key: "Ball", value: "Ball"},
                        {key: "Hinge", value: "Hinge"},
                        {key: "Slider", value: "Slider"},
                    ]
                },
                joint: {
                    volumes: [],
                    type: "Rigid",
                    angle: 90,
                    sliderLength: 1,
                    hingeAxis: new Vector3(0, 1, 0),
                    pointOfAction: new Vector3(),
                    pointOfControl: new Vector3(),
                    initialDirection: new Vector3(),
                    useCustomBone: false,
                    usedModels: [],
                    currentPointMatrix: new Matrix4(),
                    children: []
                },
                modalShift: {
                    x: 1.0,
                    y: 0.25
                },
                minimized: false,
                visible: false
            }
        },

        created() {
            this.bus.$on('show', this.show);
            this.bus.$on('hideAll', this.hide);

            this.viewContainer = new Group();
            VR.scene.add(this.viewContainer);
        },

        methods: {
            ...mapActions('kinematics', {
                addJointToChain: 'addJointToChain',
                runDemo: 'runDemo',
                stopDemo: 'stopDemo',
            }),

            show() {
                this.visible = true;
            },

            hide() {
                this.minimized = false;
                this.visible = false;
                this.reset();

                if (this.state.runningDemo) {
                    this.executeDemo();
                }

                if (this.state.volumingPicking) {
                    this.onClickVolumePick();
                }
            },

            addJoint() {
                this.addJointToChain({
                    id: this.chainID,
                    joint: this.joint
                });
                this.reset();
            },

            reset() {
                this.joint.volumes.splice(0, this.joint.volumes.length);
                this.joint.type = "Rigid";
                this.joint.angle = 90;
                this.joint.sliderLength = 1;
                this.joint.hingeAxis.set(0, 1, 0);
                this.joint.pointOfAction.set(0, 0, 0);
                this.joint.pointOfControl.set(0, 0, 0);
                this.joint.initialDirection.set(0, 0, 0);
                this.joint.useCustomBone = false;
                this.joint.currentPointMatrix.identity();
            },

            volumePickingCallback(event) {
                let pick = event.pickingData;

                if (pick.volume && !this.joint.volumes.includes(pick.volume.branchID)) {
                    let volumeList = [];
                    const branchId = pick.volume.branchID;

                    if (this.flags.selectGroup) {
                        let groupData = GroupData.getGroupDataFromBranchId(branchId);

                        if (groupData) {
                            volumeList.push(...groupData.volumes);
                        } else {
                            volumeList.push(branchId);
                        }

                    } else {
                        volumeList.push(branchId);
                    }

                    volumeList = volumeList.filter(volume => !this.joint.volumes.includes(volume));

                    if (this.flags.selectMultiple) {
                        this.joint.volumes.push(...volumeList);
                    } else {
                        this.joint.volumes.splice(0, this.joint.volumes.length, ...volumeList);
                    }

                    this.joint.usedModels.splice(0, this.joint.usedModels.length, ...this.joint.volumes.map(branchID => getModelIdFromBranchId(branchID)));

                    if (!this.flags.selectMultiple) {
                        this.onClickVolumePick();
                    }
                }
            },

            onClickVolumePick() {
                this.state.volumingPicking = !this.state.volumingPicking;

                if (this.state.volumingPicking) {
                    wv.mouseClickCallbackHandler.handleCallback({
                        callback: this.volumePickingCallback
                    }, true);
                } else {
                    wv.mouseClickCallbackHandler.handleCallback({
                        callback: this.volumePickingCallback
                    }, false);
                }
            },

            updateHighlightedMesh() {
                if (this.mesh) {
                    this.mesh.destroy();
                    this.mesh = null;
                }

                if (this.joint.volumes.length > 0) {
                    this.mesh = new DummyMesh({
                        volumeList: this.joint.volumes.map(branchId => modelManager.getVolume(branchId)),
                        branchIdList: this.joint.volumes
                    }, {retainColor: false, transparent: true});
                    wv.rootObject.add(this.mesh);
                }
            },

            executeDemo() {
                if (this.state.runningDemo) {
                    this.viewContainer.visible = true;
                    this.mesh.visible = true;
                    this.stopDemo();
                    this.state.runningDemo = false;
                } else {
                    this.viewContainer.visible = false;
                    this.mesh.visible = false;
                    this.runDemo(this.joint);
                    this.state.runningDemo = true;
                }
            },

            toggleSettingsMenu(state) {
                if (state !== true && state !== false) {
                    state = !this.$refs["volumePickingSettings"].classList.contains("active");
                }

                if (state) {
                    this.$refs["volumePickingSettings"].classList.add("active");
                } else {
                    this.$refs["volumePickingSettings"].classList.remove("active");
                }
            }
        }
    }
</script>

<style scoped>
    .v-text-field >>> input {
        margin: 0;
    }

    fieldset {
        border-color: lightgray;
        border-radius: 10px;
        padding: 5px 3px;
    }
</style>
